import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { FormHelperText } from "@mui/material";
import {
  createCountryDocumentAction,
  updateCountryDocumentAction,
} from "../../Actions/CountryDocumentActions";

import { listCountriesAction } from "../../Actions/CountriesActions";
import { listDocumentTypesAction } from "../../Actions/DocumentTypeActions";

// Helper: Compare old vs new values (Deep comparison)
const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const AddEditCountryDocument = ({
  open,
  handleClose,
  editData,
  setSnackbar,
  refresh,
}) => {
  const [countries, setCountries] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  // ✅ Fetch dropdown data
  useEffect(() => {
    // Countries
    listCountriesAction()
      .then((countryRes) => {
        setCountries(countryRes?.data?.data?.rows || []);
      })
      .catch((err) => {
        console.error("Country fetch error", err);
      });

    // Document Types
    listDocumentTypesAction()
      .then((docTypeRes) => {
        setDocumentTypes(docTypeRes?.data?.data || []);
      })
      .catch((err) => {
        console.error("Document types fetch error", err);
      });

  }, []);


  useEffect(() => {
    if (editData && countries.length && documentTypes.length) {
      const initialValuesToSet = {
        country_id: editData?.country?.id || "",
        document_type_id: editData?.documentType?.id || "",
        is_supported: editData?.is_supported ?? true,
        is_double_sided: editData?.is_double_sided ?? false,
      };
      formik.setValues(initialValuesToSet);
      setInitialFormValues(initialValuesToSet);
    }
  }, [editData, countries, documentTypes]);

  const formik = useFormik({
    initialValues: {
      country_id: editData?.country?.id || "",
      document_type_id: editData?.documentType?.id || "",
      is_supported: editData?.is_supported ?? true,
      is_double_sided: editData?.is_double_sided ?? false,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      country_id: yup
        .string()
        .required("Country is required")
        .nullable(),

      document_type_id: yup
        .string()
        .required("Document type is required")
        .nullable(),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      try {
        let response;
        const payload = { ...values };

        if (editData) {
          const updatedChangedFields = { ...changedFields };

          // Replace country_id with country name
          if (updatedChangedFields.country_id) {
            const oldCountry = countries.find(
              (c) => String(c.id) === String(updatedChangedFields.country_id.old)
            );

            const newCountry = countries.find(
              (c) => String(c.id) === String(updatedChangedFields.country_id.new)
            );

            updatedChangedFields.country = {
              old: oldCountry?.name || "",
              new: newCountry?.name || "",
            };

            delete updatedChangedFields.country_id;
          }

          // Replace document_type_id with document type name
          if (updatedChangedFields.document_type_id) {
            const oldDocumentType = documentTypes.find(
              (d) => String(d.id) === String(updatedChangedFields.document_type_id.old)
            );

            const newDocumentType = documentTypes.find(
              (d) => String(d.id) === String(updatedChangedFields.document_type_id.new)
            );

            updatedChangedFields.document_type = {
              old: oldDocumentType?.name || "",
              new: newDocumentType?.name || "",
            };

            delete updatedChangedFields.document_type_id;
          }

          payload.changedFields = JSON.stringify(
            Object.keys(updatedChangedFields)
          );

          payload.comparisonData = JSON.stringify(updatedChangedFields);

          response = await updateCountryDocumentAction(editData.id, payload);
        } else {
          response = await createCountryDocumentAction(payload);
        }


        setSnackbar({
          open: true,
          message: response?.data?.message || response?.message || "Success",
          severity: "success",
        });

        handleClose();
        refresh();

      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || "Error",
          severity: "error",
        });
      }

      setLoading(false);
    }
  });

  // Track changed fields for audit/update logging
  useEffect(() => {
    if (!editData || !initialFormValues) return;

    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, editData]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {editData ? "Update" : "Create"} Country Document
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>

          {/* ✅ Country Dropdown */}
          <FormControl
            fullWidth
            margin="normal"
            error={Boolean(formik.touched.country_id && formik.errors.country_id)}
          >
            <InputLabel>Country</InputLabel>

            <Select
              name="country_id"
              value={formik.values.country_id}
              onChange={(e) =>
                formik.setFieldValue("country_id", e.target.value)
              }
              onBlur={formik.handleBlur}
              label="Country"
            >
              {countries?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>
              {formik.touched.country_id && formik.errors.country_id}
            </FormHelperText>
          </FormControl>

          {/* ✅ Document Type Dropdown */}
          <FormControl
            fullWidth
            margin="normal"
            error={Boolean(
              formik.touched.document_type_id &&
              formik.errors.document_type_id
            )}
          >
            <InputLabel>Document Type</InputLabel>

            <Select
              name="document_type_id"
              value={formik.values.document_type_id}
              onChange={(e) =>
                formik.setFieldValue("document_type_id", e.target.value)
              }
              onBlur={formik.handleBlur}
              label="Document Type"
            >
              {documentTypes?.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>
              {formik.touched.document_type_id &&
                formik.errors.document_type_id}
            </FormHelperText>
          </FormControl>

          {/* ✅ Checkboxes */}
          <FormControlLabel
            control={
              <Checkbox
                name="is_supported"
                checked={formik.values.is_supported}
                onChange={formik.handleChange}

              />
            }
            label="Supported"
          />

          <FormControlLabel
            control={
              <Checkbox
                name="is_double_sided"
                checked={formik.values.is_double_sided}
                onChange={formik.handleChange}
              />
            }
            label="Double Sided"
          />

        </DialogContent>

        <DialogActions className="px-7 py-5 d-flex justify-content-between border-top-1 border">

          {/* CANCEL BUTTON */}
          <button
            type="button"
            className="btn btn-sm btn-outline btn-outline-primary"
            onClick={handleClose}
          >
            Cancel
          </button>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={loading}
          >
            {editData ? "Update" : "Add"}

            {loading && (
              <span
                className="spinner-border spinner-border-sm ms-2"
                role="status"
              />
            )}
          </button>

        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditCountryDocument;